import { useCallback, useMemo, useState, type ReactNode } from "react";

import {
  calculateTci,
  getWorkflowDefaults,
  isRegionId,
  isWorkflowId,
  type TciInputs,
  type TciResult,
  type WorkflowId,
} from "@/lib/tci";
import { parseNumericInput } from "@/lib/tci/parse";
import {
  TCI_SESSION_STORAGE_KEY,
  TciSessionContext,
  type DeepPartialSession,
  type TciSession,
} from "@/providers/tci-session-context";

const DEFAULT_LABOR = {
  headcount: "",
  timePercent: 45,
  salary: "",
};

const DEFAULT_YIELD = {
  volume: "",
  penaltyPerEvent: "",
};

const DEFAULT_UNTAPPED = {
  volume: "",
  actionablePercent: 2,
  valuePerAction: "",
};

function createDraftSession(): TciSession {
  return {
    workflowId: null,
    regionId: null,
    businessSector: null,
    organisationName: "",
    labor: { ...DEFAULT_LABOR },
    yieldPenalty: { ...DEFAULT_YIELD },
    untapped: { ...DEFAULT_UNTAPPED },
    annualEbitda: "",
    email: "",
    consent: false,
  };
}

function createEmptySession(
  workflowId: WorkflowId,
  profile?: Pick<
    TciSession,
    "regionId" | "businessSector" | "organisationName"
  >,
): TciSession {
  const defaults = getWorkflowDefaults(workflowId);
  return {
    workflowId,
    regionId: profile?.regionId ?? null,
    businessSector: profile?.businessSector ?? null,
    organisationName: profile?.organisationName ?? "",
    labor: {
      headcount: "",
      timePercent: defaults.laborTimePercent,
      salary: "",
    },
    yieldPenalty: {
      volume: "",
      penaltyPerEvent: "",
    },
    untapped: {
      volume: "",
      actionablePercent: defaults.untappedActionablePercent,
      valuePerAction: "",
    },
    annualEbitda: "",
    email: "",
    consent: false,
  };
}

function normalizeSession(parsed: TciSession): TciSession | null {
  const workflowId =
    parsed.workflowId && isWorkflowId(parsed.workflowId)
      ? parsed.workflowId
      : null;
  const regionId =
    parsed.regionId && isRegionId(parsed.regionId) ? parsed.regionId : null;
  const businessSector =
    typeof parsed.businessSector === "string" && parsed.businessSector.trim()
      ? parsed.businessSector
      : null;
  const organisationName =
    typeof parsed.organisationName === "string" ? parsed.organisationName : "";

  // Draft intro session (no workflow yet) or full calculator session.
  if (!workflowId && !regionId && !businessSector && !organisationName.trim()) {
    return null;
  }

  return {
    ...createDraftSession(),
    ...parsed,
    workflowId,
    regionId,
    businessSector,
    organisationName,
    labor: { ...DEFAULT_LABOR, ...parsed.labor },
    yieldPenalty: { ...DEFAULT_YIELD, ...parsed.yieldPenalty },
    untapped: { ...DEFAULT_UNTAPPED, ...parsed.untapped },
  };
}

function readSession(): TciSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(TCI_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TciSession;
    const normalized = normalizeSession(parsed);
    if (!normalized) {
      window.sessionStorage.removeItem(TCI_SESSION_STORAGE_KEY);
      return null;
    }
    return normalized;
  } catch {
    return null;
  }
}

function writeSession(session: TciSession | null) {
  if (typeof window === "undefined") return;
  try {
    if (!session) {
      window.sessionStorage.removeItem(TCI_SESSION_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(
      TCI_SESSION_STORAGE_KEY,
      JSON.stringify(session),
    );
  } catch {
    // Ignore quota / private mode failures.
  }
}

function sessionToInputs(session: TciSession): TciInputs | null {
  if (!session.workflowId) return null;
  return {
    workflowId: session.workflowId,
    labor: {
      headcount: parseNumericInput(session.labor.headcount),
      timePercent: session.labor.timePercent,
      salary: parseNumericInput(session.labor.salary),
    },
    yieldPenalty: {
      volume: parseNumericInput(session.yieldPenalty.volume),
      penaltyPerEvent: parseNumericInput(session.yieldPenalty.penaltyPerEvent),
    },
    untapped: {
      volume: parseNumericInput(session.untapped.volume),
      actionablePercent: session.untapped.actionablePercent,
      valuePerAction: parseNumericInput(session.untapped.valuePerAction),
    },
    annualEbitda: parseNumericInput(session.annualEbitda),
  };
}

export function TciSessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<TciSession | null>(() =>
    readSession(),
  );

  const startSession = useCallback((workflowId: WorkflowId) => {
    setSession((prev) => {
      const next = createEmptySession(workflowId, {
        regionId: prev?.regionId ?? null,
        businessSector: prev?.businessSector ?? null,
        organisationName: prev?.organisationName ?? "",
      });
      writeSession(next);
      return next;
    });
  }, []);

  const updateSession = useCallback((partial: DeepPartialSession) => {
    setSession((prev) => {
      const base = prev ?? createDraftSession();
      const next: TciSession = {
        ...base,
        ...partial,
        labor: { ...base.labor, ...partial.labor },
        yieldPenalty: { ...base.yieldPenalty, ...partial.yieldPenalty },
        untapped: { ...base.untapped, ...partial.untapped },
      };
      writeSession(next);
      return next;
    });
  }, []);

  const resetSession = useCallback(() => {
    setSession(null);
    writeSession(null);
  }, []);

  const buildTciInputs = useCallback((): TciInputs | null => {
    if (!session) return null;
    return sessionToInputs(session);
  }, [session]);

  const result: TciResult | null = useMemo(() => {
    if (!session) return null;
    const inputs = sessionToInputs(session);
    if (!inputs) return null;
    return calculateTci(inputs);
  }, [session]);

  const value = useMemo(
    () => ({
      session,
      startSession,
      updateSession,
      resetSession,
      buildTciInputs,
      result,
    }),
    [
      session,
      startSession,
      updateSession,
      resetSession,
      buildTciInputs,
      result,
    ],
  );

  return (
    <TciSessionContext.Provider value={value}>
      {children}
    </TciSessionContext.Provider>
  );
}
